<template>
	<div class="h-full">
		<component class="h-full" :is="layout">
			<header-component />
			<Sidebar v-if="!hideSidebar" />
			<router-view />
		</component>
		<network-error-notification v-if="networkErrorNotification" />
		<validation-error-dialog
			v-if="showValidationDialog"
			:message="validationError.message"
			:description="validationError.description"
			:title="validationError.title"
			@close="hideValidationDialog"
		/>
		<pwa-dialog v-if="showPWADialog" />
		<post-view v-if="showDetailPage" />
		<dialog-component v-if="balanceNotification" @close="closeBalanceNotification">
			<template #header-title v-if="balance > 0">{{ $t('enoughLikes', { count: likesCount }) }}</template>
			<template #header-title v-else>{{ $t('notEnoughLikes') }}</template>
			<notification-balance />
		</dialog-component>
		<template v-if="$auth.check('girl')">
			<create-post-dialog @close="closeCreatePostModal" v-if="showCreatePostDialog && !isVideoUploading" />
			<create-post-loader-dialog v-if="showCreatePostDialog && isVideoUploading" />
			<girl-verification-dialog v-if="showVerificationDialog" @close="closeVerificationDialog" />
			<girl-verification-qr-code-dialog v-if="showVerificationQrCodeDialog" @close="closeVerificationDialog" />
			<girl-verification-needs-dialog v-if="showVerificationNeedsDialog" @close-dialog="closeVerificationNeedsDialog" />
			<link-greetings-dialog v-if="showLinkGreetingsDialog" @close="closeLinkGreetingsDialog" />
			<girl-verification-status-dialog v-if="showVerificationStatusDialog" @close="closeVerificationStatusDialog" />
		</template>
		<the-app-loader />
		<tutorial-component v-if="showTutorialSlider" :slides="tutorialSlides" @end="onFinishTutorial" />
		<consumers-lists-dialog v-if="showConsumersListsDialog" @close="closeConsumersListsDialog" />
		<consumer-tutorial-component
			v-if="consumerTutorial"
			@close="closeTutorial"
			@shown="shownTutorial"
			@end="closeTutorial((isEnded = true))"
		/>
		<sub-limit-dialog v-if="showSubLimitModal" @decline="closeSubLimitModal" @close="closeSubLimitModal" />
		<sub-limit-error-dialog
			v-if="showSubLimitErrorModal"
			@decline="closeSubLimitErrorModal"
			@close="closeSubLimitErrorModal"
		/>
		<confirm-age-dialog v-if="showConfirmAgeDialog" />
		<confirm-unfollow-dialog v-if="showUnfollowDialog" />
		<unsubscribe-and-unfollow-dialog v-if="showUnsubscribeAndUnfollowDialog" />
		<DialogPopup />
		<comments-dialog :show-comments-dialog="showCommentsDialog" @close="hideCommentsDialog" />
		<consumers-with-likes-dialog :show-post-likes-dialog="showPostLikesDialog" @close="hidePopUpPostLikes" />
	</div>
</template>

<script>
import { mapActions, mapState, mapWritableState } from 'pinia'
import { useModalStore } from '@/stores/modal'
import { useVideoUploadStore } from '@/stores/videoUpload'
import { useCreatePostStore } from '@/stores/createPost'
import { useCommonStore } from '@/stores/common'
import { useTutorialStore } from '@/stores/tutorial'
import { useConsumerTutorialStore } from '@/stores/consumerTutorial'
import { useNotificationsStore } from '@/stores/notifications'
import { useCurrentUserStore } from '@/stores/user'
import { usePostsStore } from '@/stores/posts'
import { useProfileStore } from '@/stores/profile'
import { detectIOS, detectSafari } from '@/helpers/detectPlatform'
import NetworkErrorNotification from '@/components/NetworkErrorNotification.vue'
import CreatePostDialog from '@/components/dialogs/CreatePostDialog.vue'
import NotificationBalance from '@/components/NotificationBalance.vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import pwaDialog from '@/components/dialogs/pwaDialog.vue'
import CreatePostLoaderDialog from '@/components/dialogs/CreatePostLoaderDialog.vue'
import GirlVerificationDialog from '@/components/dialogs/GirlVerificationDialog.vue'
import GirlVerificationQrCodeDialog from '@/components/dialogs/GirlVerificationQrCodeDialog.vue'
import ValidationErrorDialog from '@/components/dialogs/ValidationErrorDialog.vue'
import TheAppLoader from '@/components/TheAppLoader.vue'
import TutorialComponent from '@/components/TutorialComponent.vue'
import GirlVerificationNeedsDialog from '@/components/dialogs/GirlVerificationNeedsDialog.vue'
import ConsumerTutorialComponent from '@/components/ConsumerTutorialComponent.vue'
import ButtonComponent from '@/components/ButtonComponent.vue'
import LinkGreetingsDialog from '@/components/dialogs/LinkGreetingsDialog.vue'
import GirlVerificationStatusDialog from '@/components/dialogs/GirlVerificationStatusDialog.vue'
import PostView from '@/views/PostView.vue'
import Sidebar from '@/components/desktop/Sidebar.vue'
import HeaderComponent from '@/components/HeaderComponent.vue'
import HeaderLogo from '@/components/HeaderLogo.vue'
import ConsumersListsDialog from '@/components/dialogs/ConsumersListsDialog.vue'
import SubLimitDialog from '@/components/dialogs/SubLimitDialog.vue'
import SubLimitErrorDialog from '@/components/dialogs/SubLimitErrorDialog.vue'
import ConfirmAgeDialog from '@/components/dialogs/ConfirmAgeDialog.vue'
import ConfirmUnfollowDialog from '@/components/dialogs/ConfirmUnfollowDialog.vue'
import UnsubscribeAndUnfollowDialog from '@/components/dialogs/UnsubscribeAndUnfollowDialog.vue'
import DialogPopup from '@/components/dialogs/DialogGenerator.vue'
import CommentsDialog from '@/components/dialogs/CommentsDialog.vue'
import ConsumersWithLikesDialog from '@/components/dialogs/ConsumersWithLikesDialog.vue'

export default {
	components: {
		UnsubscribeAndUnfollowDialog,
		ConfirmUnfollowDialog,
		ConfirmAgeDialog,
		ConsumersListsDialog,
		GirlVerificationNeedsDialog,
		TutorialComponent,
		TheAppLoader,
		GirlVerificationDialog,
		GirlVerificationQrCodeDialog,
		CreatePostLoaderDialog,
		DialogComponent,
		pwaDialog,
		CreatePostDialog,
		NetworkErrorNotification,
		NotificationBalance,
		ButtonComponent,
		ConsumerTutorialComponent,
		LinkGreetingsDialog,
		GirlVerificationStatusDialog,
		PostView,
		Sidebar,
		HeaderComponent,
		HeaderLogo,
		ValidationErrorDialog,
		SubLimitDialog,
		SubLimitErrorDialog,
		DialogPopup,
		CommentsDialog,
		ConsumersWithLikesDialog
	},
	computed: {
		...mapState(useTutorialStore, ['tutorialSlides', 'showTutorialSlider']),
		...mapState(useConsumerTutorialStore, ['consumerTutorial']),
		...mapState(useVideoUploadStore, ['isVideoUploading']),
		...mapState(useCreatePostStore, ['showValidationDialog', 'validationError', 'scrollY']),
		...mapState(useProfileStore, ['balance']),
		...mapState(useModalStore, [
			'showConsumersListsDialog',
			'networkErrorNotification',
			'showVerificationDialog',
			'showVerificationQrCodeDialog',
			'balanceNotification',
			'showVerificationNeedsDialog',
			'showPWADialog',
			'showLinkGreetingsDialog',
			'showVerificationStatusDialog',
			'showDetailPage',
			'showSubLimitModal',
			'showSubLimitErrorModal',
			'showConfirmAgeDialog',
			'showUnfollowDialog',
			'showUnsubscribeAndUnfollowDialog',
			'showCommentsDialog',
			'showPostLikesDialog'
		]),
		...mapState(useCreatePostStore, ['showCreatePostDialog']),
		...mapState(useCurrentUserStore, {
			isAuthorised: 'isAuthorised',
			currentUser: 'currentUser'
		}),
		...mapWritableState(useCommonStore, ['isPowerSaveMode']),
		layout() {
			return this.$route.meta.layout || 'default'
		},
		hideSidebar() {
			return this.$route.meta.hideSidebar
		},
		likesCount() {
			return `${this.balance} ${this.$t('like', this.balance)}`
		}
	},
	methods: {
		...mapActions(useCurrentUserStore, {
			fetchCurrentUser: 'fetchCurrentUser'
		}),
		...mapActions(useTutorialStore, ['startTutorial', 'finishTutorial']),
		...mapActions(useConsumerTutorialStore, ['setTutorial', 'finishTutorial']),
		...mapActions(useCreatePostStore, ['closeCreatePostDialog', 'hideValidationDialog', 'setScrollY']),
		...mapActions(useCommonStore, {
			setDeferredPrompt: 'setDeferredPrompt',
			setProfileLocale: 'setProfileLocale'
		}),
		...mapActions(useModalStore, {
			openVerificationDialog: 'openVerificationDialog',
			closeVerificationDialog: 'closeVerificationDialog',
			closeBalanceNotification: 'closeBalanceNotification',
			closeVerificationNeedsDialog: 'closeVerificationNeedsDialog',
			closeLinkGreetingsDialog: 'closeLinkGreetingsDialog',
			closeVerificationStatusDialog: 'closeVerificationStatusDialog',
			closeConsumersListsDialog: 'closeConsumersListsDialog',
			openSubLimitModal: 'openSubLimitModal',
			closeSubLimitModal: 'closeSubLimitModal',
			closeSubLimitErrorModal: 'closeSubLimitErrorModal',
			closeCommentsDialog: 'closeCommentsDialog',
			closePostLikesDialog: 'closePostLikesDialog'
		}),
		...mapActions(useNotificationsStore, {
			fetchChatPushState: 'fetchChatPushState'
		}),
		...mapActions(usePostsStore, {
			sendViewedPost: 'sendViewedPost'
		}),
		async onFinishTutorial() {
			const finishedTutorialName = await this.finishTutorial('latest-slider')

			if (finishedTutorialName === 'secondStep') {
				await this.startTutorial('thirdStep')
			}
		},
		closeTutorial(slideIndex, isEnded) {
			this.setTutorial(null)
			if (isEnded) this.$metrika.reachGoal('onboarding_button_start')
			else this.$metrika.reachGoal(`onbording-close${slideIndex}`)
		},
		shownTutorial() {
			this.finishTutorial('onboarding')
		},
		captureEvent() {
			if (localStorage.getItem('pwaAsk') === null) {
				window.addEventListener('beforeinstallprompt', (e) => {
					e.preventDefault()
					this.setDeferredPrompt(e)
				})
			}
		},
		closeCreatePostModal() {
			this.closeCreatePostDialog()
			if (this.scrollY) {
				this.$nextTick(() => {
					window.scrollTo(0, this.scrollY)
					this.setScrollY(null)
				})
			}
		},
		getLocale() {
			if (this.$config.localizationEnabled) {
				const { locale: savedLocale } = this.currentUser
				const localLocale = localStorage.getItem('currentLocale')
				const browserLocale = navigator.language.includes('-')
					? navigator.language.split(/[-_]/)[0]
					: navigator.language

				if (savedLocale) {
					return savedLocale
				}
				if (localLocale) {
					return localLocale
				}
				return browserLocale
			}
			return this.$config.defaultLanguage
		},
		async setLocale() {
			const locale = this.getLocale()
			this.$i18n.locale = locale
			if (this.$config.localizationEnabled) {
				await this.setProfileLocale(locale)
				localStorage.setItem('currentLocale', locale)
			}
		},
		hideCommentsDialog(link) {
			this.closeCommentsDialog()
			if (window.history.state?.dialogOpen && !link) {
				window.history.back()
			}
		},
		hidePopUpPostLikes(link) {
			this.closePostLikesDialog()
			if (window.history.state?.dialogOpen && !link) {
				window.history.back()
			}
		}
	},
	watch: {
		isAuthorised: {
			async handler(val) {
				if (val) {
					await this.fetchCurrentUser()
					await this.setLocale()
					await this.fetchChatPushState()
				}
			},
			immediate: true
		}
	},
	async beforeMount() {
		if ('serviceWorker' in navigator) {
			await navigator.serviceWorker
				.register('/service-worker.js', { scope: '/', type: 'module' })
				.then(() => {
					const userData = {
						token: this.$auth.token(),
						sdpUserId: this.currentUser?.sdp_user_id
					}
					window.addEventListener('focus', async () => {
						navigator.serviceWorker.ready.then((sw) => {
							sw.active.postMessage({ isPageVisible: true, userData })
						})
					})
					window.addEventListener('blur', async () => {
						navigator.serviceWorker.ready.then((sw) => {
							sw.active.postMessage({ isPageVisible: false, userData })
						})
					})
				})
				.catch((e) => {
					throw new Error(`${e.toString()} service_worker_error`)
				})
		} else {
			throw new Error('Service workers are not supported.')
		}
	},
	mounted() {
		if (detectSafari()) {
			const r = document.querySelector('body')
			r.style.fontFamily = `'Inter', JoyPixelsSBIX, JoyPixelsSVG, sans-serif`
		}
		const video = document.createElement('video')
		video.src = '#'
		video.controls = false
		video.muted = true
		video.playsInline = true
		document.body.appendChild(video)
		video
			.play()
			.then(() => {})
			.catch((e) => {
				if (detectIOS() && e.toString().includes('NotAllowedError')) {
					this.isPowerSaveMode = true
				}
			})
			.finally(() => {
				video.remove()
			})
		this.captureEvent()
		window.addEventListener('beforeunload', () => {
			this.sendViewedPost()
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker.ready.then((sw) => {
					const userData = {
						token: this.$auth.token(),
						sdpUserId: this.currentUser?.sdp_user_id
					}
					sw.active.postMessage({ isPageVisible: false, userData })
				})
			}
		})

		if (!this.isAuthorised && this.$config.localizationEnabled) {
			const localLocale = localStorage.getItem('currentLocale')
			const browserLocale = navigator.language.includes('-') ? navigator.language.split(/[-_]/)[0] : navigator.language

			if (this.$i18n.availableLocales.includes(localLocale) || this.$i18n.availableLocales.includes(browserLocale)) {
				if (localLocale) {
					this.$i18n.locale = localLocale
				} else {
					this.$i18n.locale = browserLocale
				}
			}
			localStorage.setItem('currentLocale', localLocale || browserLocale)
		}
	}
}
</script>
